import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import TextField from '@material-ui/core/TextField';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  useApi,
  useApiHolder,
  identityApiRef,
  microsoftAuthApiRef,
} from '@backstage/core-plugin-api';
import {
  FieldExtensionComponentProps,
  CustomFieldValidator,
} from '@backstage/plugin-scaffolder-react';

/** The tenant spelling `reader_upns` — Terraform's azuread_user lookup — needs. */
const ENTRA_DOMAIN = '@arkancesas.onmicrosoft.com';
/** What Backstage profiles carry: the Entra user's `mail`, not their UPN. */
const BACKSTAGE_DOMAIN = '@arkance.world';

/**
 * The Azure account an address stands for, as the subscription tfvars will
 * spell it. Same rule as the template's `replace` and the backend's
 * normalizeSubscriptionViewers, so the row shows exactly what is granted.
 *
 * Showing `@arkance.world` was misleading: the access goes to the
 * `@arkancesas.onmicrosoft.com` account, and people signed in to the Azure
 * portal with their arkance.world address and found nothing there.
 */
export function toEntraUpn(value: string): string {
  const trimmed = value.trim();
  return trimmed.toLowerCase().endsWith(BACKSTAGE_DOMAIN)
    ? `${trimmed.slice(0, -BACKSTAGE_DOMAIN.length)}${ENTRA_DOMAIN}`
    : trimmed;
}

/**
 * The signed-in user's UPN, from the Microsoft ID token's `preferred_username`.
 *
 * Backstage signs people in against the same Entra tenant the subscriptions
 * live in, so this is the exact account `reader_upns` must name — no guessing
 * from the mail address. Undefined when the token is missing or unreadable.
 */
export function upnFromIdToken(idToken: string | undefined): string | undefined {
  const payload = idToken?.split('.')[1];
  if (!payload) return undefined;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const claims = JSON.parse(atob(padded));
    const upn = claims.preferred_username ?? claims.upn;
    return typeof upn === 'string' && upn.includes('@') ? upn : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Whether to seed the viewer list with the logged-in user's own address.
 *
 * Pulled out of the effect below because all three conditions guard against a
 * different, silent failure:
 *
 * - `prefillSubmitter` is Day-1 only. On Day-2 the list arrives complete, and an
 *   empty one means every viewer was removed on purpose — prefilling there puts
 *   whoever pressed Modify onto a subscription nobody granted them, which is the
 *   exact bug this whole change removed.
 * - `alreadyPrefilled` latches after the first run. Without it, deleting the
 *   prefilled row grows it straight back and the field refuses to let you take
 *   yourself off the list.
 * - a non-empty list is somebody's actual state and is never overwritten.
 */
export function shouldPrefillSubmitter(
  prefillSubmitter: boolean,
  alreadyPrefilled: boolean,
  emails: string[],
): boolean {
  return prefillSubmitter && !alreadyPrefilled && emails.length === 0;
}

/**
 * The complete list of people who may read the app's Azure subscription.
 *
 * There is no hidden entry and no locked row: the value of this field is
 * rendered verbatim into `reader_upns`, so what is on screen is what gets
 * granted, and deleting a row is what revokes someone.
 *
 * `prefillSubmitter` (Day-1 only) seeds an EMPTY list with the logged-in user's
 * address as an ordinary, deletable row. Without it the person creating an app
 * would end up with no read access to the subscription they just created; with
 * it on Day-2 — where the list arrives already complete — whoever pressed
 * Modify would be spliced into a list they were never on.
 */
export const SubscriptionViewersField = ({
  onChange,
  rawErrors,
  formData,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<string[]>) => {
  const identityApi = useApi(identityApiRef);
  // Optional: the field still works, from the profile address, where no
  // Microsoft sign-in is registered.
  const apiHolder = useApiHolder();
  const [prefillError, setPrefillError] = useState(false);
  // Memoized because the effect below depends on it: `formData ?? []` hands back
  // a brand-new array on every render while the field is empty, which is exactly
  // when the prefill effect is live.
  const emails: string[] = useMemo(() => formData ?? [], [formData]);

  const prefillSubmitter =
    (uiSchema?.['ui:options']?.prefillSubmitter as boolean | undefined) ?? false;

  // Latches only after the address was written, not before the request. A latch
  // set before the await survives a discarded result, and the field then waits
  // for a prefill that already gave up. See shouldPrefillSubmitter.
  const prefilled = useRef(false);
  // The enclosing form re-renders while the profile request is in flight, so
  // the effect below runs again. This stops it asking a second time.
  const requested = useRef(false);

  // `onChange` is a new function on every render of the enclosing form. The
  // effect must call the current one, so it reads it from a ref instead of
  // depending on it.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // Only an unmount discards the result. A dependency change must not, because
  // that is what the enclosing form does on every keystroke elsewhere on the
  // page, and the address would never be written.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !shouldPrefillSubmitter(
        prefillSubmitter,
        prefilled.current || requested.current,
        emails,
      )
    ) {
      return;
    }
    requested.current = true;
    // The UPN from the sign-in token is the account that gets the access. The
    // profile address is only a fallback, spelled the way the tfvars will be.
    const ownUpn = async (): Promise<string | undefined> => {
      try {
        const upn = upnFromIdToken(
          await apiHolder
            .get(microsoftAuthApiRef)
            ?.getIdToken({ optional: true }),
        );
        if (upn) return upn;
      } catch {
        // No Microsoft session in this tab: fall back to the profile.
      }
      const profile = await identityApi.getProfileInfo();
      return profile.email ? toEntraUpn(profile.email) : undefined;
    };
    ownUpn()
      .then(upn => {
        if (!mounted.current) return;
        if (upn) {
          onChangeRef.current([upn]);
          prefilled.current = true;
        } else {
          setPrefillError(true);
        }
      })
      .catch(() => {
        // Not fatal: the list is editable, so the user can type their own
        // address. Saying so is better than an empty box with no explanation.
        if (mounted.current) setPrefillError(true);
      });
    // `emails` re-runs this on every edit, which is harmless: the two refs make
    // every run after the first a no-op.
  }, [identityApi, apiHolder, prefillSubmitter, emails]);

  const updateEmail = (index: number, value: string) => {
    const next = [...emails];
    next[index] = value;
    onChange(next);
  };

  // A typed @arkance.world address is shown as the account it will become once
  // the user leaves the row, so the list reads as what is granted.
  const normalizeEmail = (index: number) => {
    const current = emails[index] ?? '';
    const upn = toEntraUpn(current);
    if (upn !== current) updateEmail(index, upn);
  };

  const removeEmail = (index: number) => {
    onChange(emails.filter((_, i) => i !== index));
  };

  const addEmail = () => {
    onChange([...emails, '']);
  };

  const hasFormErrors = !!rawErrors?.length;
  const title = schema.title ?? 'Subscription resource viewers';

  return (
    <FormControl fullWidth error={hasFormErrors}>
      <InputLabel shrink>{title}</InputLabel>

      {emails.map((email, index) => {
        // Compared as the accounts they become: x@arkance.world and
        // x@arkancesas.onmicrosoft.com are the same person.
        const trimmed = toEntraUpn(email ?? '').toLowerCase();
        const isDuplicateOfPrior =
          trimmed !== '' &&
          emails
            .slice(0, index)
            .some(prior => toEntraUpn(prior ?? '').toLowerCase() === trimmed);

        return (
          <Box key={index} mt={index === 0 ? 3 : 2}>
            <Box display="flex" alignItems="center">
              <TextField
                fullWidth
                label={`Viewer ${index + 1}`}
                placeholder="alice.smith@arkancesas.onmicrosoft.com"
                value={email ?? ''}
                onChange={e => updateEmail(index, e.target.value)}
                onBlur={() => normalizeEmail(index)}
                error={isDuplicateOfPrior}
                InputLabelProps={{ shrink: true }}
              />
              <IconButton
                aria-label="Remove email"
                onClick={() => removeEmail(index)}
                size="small"
                style={{ marginLeft: 8 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            {isDuplicateOfPrior && (
              <FormHelperText error>
                This email is already in the list above.
              </FormHelperText>
            )}
          </Box>
        );
      })}

      <Box mt={2}>
        <Button
          variant="outlined"
          size="small"
          onClick={addEmail}
          disabled={
            emails.length > 0 &&
            (emails[emails.length - 1] ?? '').trim() === ''
          }
        >
          Add viewer email
        </Button>
      </Box>

      {prefillError && (
        <FormHelperText>
          Could not read your profile email — add your own address if you need
          access to this subscription.
        </FormHelperText>
      )}
      <FormHelperText>
        Access is granted to the Azure account
        (firstname.lastname@arkancesas.onmicrosoft.com) — sign in to the Azure
        portal with that account to see the app. An @arkance.world address is
        converted to it.
      </FormHelperText>
      <FormHelperText>
        {schema.description ??
          'This is the complete list. Whoever is left here keeps read access to the subscription; anyone removed loses it.'}
      </FormHelperText>
      {hasFormErrors && <FormHelperText error>{rawErrors![0]}</FormHelperText>}
    </FormControl>
  );
};

// Form-level validation. Backstage scaffolder calls this when the user clicks
// Next / Review and uses the returned errors to block progression.
//
// Two rules: nobody has read access on Day-1, and duplicates. Your own address
// is a legitimate entry in this list, and on Day-1 the default one, so there is
// nothing else to reject here. A blank row next to a real address is tolerated
// and dropped downstream, because an empty row is what "Add viewer email"
// leaves behind before you type in it.
//
// The Day-1 rule covers an empty list and a list of nothing but blank rows,
// which come to the same thing once the blanks are dropped: a subscription
// nobody can read, the person who created the app included. The prefill can
// fail, so the form cannot assume the list is seeded.
//
// Day-2 accepts both. There the list arrives complete, and a list with nobody
// on it is an app whose viewers were all removed on purpose.
export const subscriptionViewersValidation: CustomFieldValidator<string[]> = (
  value,
  validation,
  context,
) => {
  // Rows can be undefined: a draft saved while the field still carried a
  // schema-padded row hands one back.
  const rows = (value ?? []).map(raw => raw ?? '');

  // The Modify wizard injects `_original`, so its presence means an existing
  // application. Same test as applicationDomainsValidation.
  const isDayOne = !(context?.formData as { _original?: unknown } | undefined)
    ?._original;
  if (isDayOne && rows.every(raw => raw.trim() === '')) {
    validation.addError(
      'Enter at least one viewer email address. Whoever is on this list can read the resources in this app subscription, and nobody else can.',
    );
    return;
  }

  const seen = new Set<string>();
  for (const raw of rows) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const lower = toEntraUpn(trimmed).toLowerCase();
    if (seen.has(lower)) {
      validation.addError(
        `"${trimmed}" is listed more than once. Remove duplicates to continue.`,
      );
      return;
    }
    seen.add(lower);
  }
};
